import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
} from '@cartcloser/ui';
import { superCreateTemplate, superListTemplates } from '../lib/api.js';

export const Route = createFileRoute('/super/templates/')({
  component: SuperTemplatesList,
});

function SuperTemplatesList() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['super-templates'], queryFn: superListTemplates });
  const [templateKey, setTemplateKey] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => superCreateTemplate({ templateKey, name }),
    onSuccess: async () => {
      setTemplateKey('');
      setName('');
      await queryClient.invalidateQueries({ queryKey: ['super-templates'] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed'),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Agent templates</h1>
        <p className="text-sm text-muted-foreground">
          Proprietary prompt templates. Field content here is never exposed to merchants — they only
          inherit, augment, or overwrite it.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>New template</CardTitle>
          <CardDescription>Use a stable key like “appliances.dishwashers”.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              create.mutate();
            }}
          >
            <FormField label="Template key" className="flex-1" required>
              {({ id }) => (
                <Input
                  id={id}
                  value={templateKey}
                  onChange={(e) => setTemplateKey(e.target.value)}
                  placeholder="appliances.dishwashers"
                  required
                />
              )}
            </FormField>
            <FormField label="Name" className="flex-1" required>
              {({ id }) => (
                <Input
                  id={id}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dishwashers"
                  required
                />
              )}
            </FormField>
            <Button type="submit" loading={create.isPending}>
              Create
            </Button>
          </form>
          {error ? (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid gap-3">
        {query.data?.map((t) => {
          const s = t.stats;
          const pct = (n: number, d: number) => (d > 0 ? `${Math.round((n / d) * 100)}%` : '—');
          return (
            <Link key={t.id} to="/super/templates/$id" params={{ id: t.id }} className="block">
              <Card className="transition-colors hover:border-primary/40">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.templateKey}</p>
                    </div>
                    <span className="shrink-0 text-sm font-medium">
                      {s ? `${s.agents} agent${s.agents === 1 ? '' : 's'}` : ''}
                      {s && s.stores > 0 ? (
                        <span className="text-muted-foreground"> · {s.stores} store{s.stores === 1 ? '' : 's'}</span>
                      ) : null}
                    </span>
                  </div>
                  {s && s.conversations > 0 ? (
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{s.conversations} conversations</span>
                      <span>{pct(s.engaged, s.conversations)} reached a rec</span>
                      <span>{pct(s.recsClicked, s.recsShown)} click-through</span>
                      {s.feedbackUp + s.feedbackDown > 0 ? (
                        <span>
                          {pct(s.feedbackUp, s.feedbackUp + s.feedbackDown)} satisfaction
                        </span>
                      ) : null}
                    </div>
                  ) : s && s.agents > 0 ? (
                    <p className="mt-3 text-xs text-muted-foreground">
                      No live conversations yet from these agents.
                    </p>
                  ) : (
                    <p className="mt-3 text-xs text-muted-foreground">Not used by any agents yet.</p>
                  )}
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
